/**
 * ArrowTool: Interactive tool for drawing arrow shapes
 * Feature: 049-critique-photo-markup
 *
 * Click-drag interaction: Tail on mouse down, head on mouse up
 */

import { useState, useRef, useCallback } from 'react';
import type { ArrowShape, ShapeStyle, MarkupShape } from '@/types/markup.types';
import { pixelToPercentage, transformShapeForViewBox } from '@/utils/markupRenderer';
import { validateArrow, isAccidentalClick } from '@/utils/markupValidation';
import { useMarkupDrag } from '@/hooks/useMarkupDrag';
import logger from '@/utils/logger';

interface ArrowToolProps {
  currentStyle: ShapeStyle;
  onShapeComplete: (shape: ArrowShape) => void;
  onValidationError: (message: string) => void;
  onToolClick: (x: number, y: number) => number | null;
  onShapeClick: (shapeNumber: number) => void;
  selectedShapeId: number | null;
  shapes: MarkupShape[];
  onShapeMove: (shapeNumber: number, shape: MarkupShape) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  photoAspectRatio?: number;
}

export function ArrowTool({
  currentStyle,
  onShapeComplete,
  onValidationError,
  onToolClick: _onToolClick, // Not used - hook handles hit detection internally
  onShapeClick,
  selectedShapeId,
  shapes,
  onShapeMove,
  containerRef,
  photoAspectRatio = 1,
}: ArrowToolProps) {
  const viewBoxHeight = 100 / photoAspectRatio;
  const viewBox = `0 0 100 ${viewBoxHeight}`;
  const [isDrawing, setIsDrawing] = useState(false);
  const [previewShape, setPreviewShape] = useState<ArrowShape | null>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);

  // Use shared drag handler for existing shapes and handles
  const {
    cursor: dragCursor,
    handleMouseDown: dragMouseDown,
    handleMouseMove: dragMouseMove,
    handleMouseUp: dragMouseUp,
    handleMouseLeave: dragMouseLeave,
  } = useMarkupDrag({
    shapes,
    selectedShapeId, // Pass through so hook can detect handle clicks
    onShapeClick, // Pass through to hook for direct shape selection
    onShapeMove,
    containerRef,
    photoAspectRatio,
    readOnly: false,
  });

  // Use drag cursor with fallback to crosshair for drawing
  const cursor = dragCursor ?? 'crosshair';

  /**
   * Get relative coordinates within container (percentage)
   * Accounts for object-contain letterboxing/pillarboxing
   */
  const getRelativeCoords = useCallback(
    (e: React.MouseEvent): { x: number; y: number } => {
      if (!containerRef.current) return { x: 0, y: 0 };

      const rect = containerRef.current.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const containerAspectRatio = containerWidth / containerHeight;

      // Calculate actual photo dimensions within container (object-contain behavior)
      let photoWidth, photoHeight, offsetX, offsetY;

      if (photoAspectRatio > containerAspectRatio) {
        // Photo is wider - letterboxing on top/bottom
        photoWidth = containerWidth;
        photoHeight = containerWidth / photoAspectRatio;
        offsetX = 0;
        offsetY = (containerHeight - photoHeight) / 2;
      } else {
        // Photo is taller - pillarboxing on left/right
        photoHeight = containerHeight;
        photoWidth = containerHeight * photoAspectRatio;
        offsetX = (containerWidth - photoWidth) / 2;
        offsetY = 0;
      }

      // Convert mouse position to photo-relative coordinates
      const pixelX = e.clientX - rect.left - offsetX;
      const pixelY = e.clientY - rect.top - offsetY;

      // Convert to percentage (0-100 for X, 0-viewBoxHeight for Y)
      return pixelToPercentage(pixelX, pixelY, photoWidth, photoHeight, photoAspectRatio);
    },
    [containerRef, photoAspectRatio]
  );

  /**
   * Calculate arrow head points for preview rendering
   */
  const calculateArrowHead = useCallback(
    (x1: number, y1: number, x2: number, y2: number, headSize: number = 3): string => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headAngle = Math.PI / 6; // 30 degrees

      const p1x = x2 - headSize * Math.cos(angle - headAngle);
      const p1y = y2 - headSize * Math.sin(angle - headAngle);

      const p2x = x2 - headSize * Math.cos(angle + headAngle);
      const p2y = y2 - headSize * Math.sin(angle + headAngle);

      return `${p1x},${p1y} ${x2},${y2} ${p2x},${p2y}`;
    },
    []
  );

  /**
   * Handle mouse down - check for existing shape/handle first, then start drawing arrow (tail)
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // First, check if clicking on existing shape or handle - let shared handler manage it
      const handledByDrag = dragMouseDown(e);
      if (handledByDrag) {
        logger.debug('ArrowTool', 'Click handled by drag system');
        return;
      }

      // Not clicking on shape/handle - start drawing new arrow
      const start = getRelativeCoords(e);
      startPointRef.current = start;
      setIsDrawing(true);

      // Create preview shape with zero length
      const preview: ArrowShape = {
        type: 'arrow',
        number: 0, // Will be assigned when added to shapes array
        strokeWidth: currentStyle.strokeWidth,
        strokeColor: currentStyle.strokeColor,
        strokeDasharray: currentStyle.strokeDasharray,
        geometry: {
          x1: start.x,
          y1: start.y,
          x2: start.x,
          y2: start.y,
        },
      };

      setPreviewShape(preview);
      logger.debug('ArrowTool', 'Started drawing arrow at', start);
    },
    [dragMouseDown, getRelativeCoords, currentStyle]
  );

  /**
   * Handle mouse move - update preview arrow or delegate to drag handler
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Delegate to drag handler (handles shape/handle dragging)
      dragMouseMove(e);

      // Handle drawing new shape
      if (!isDrawing || !startPointRef.current || !previewShape) return;

      const current = getRelativeCoords(e);

      // Capture start point value before state update to avoid race condition
      const startPoint = startPointRef.current;

      setPreviewShape((prev) =>
        prev
          ? {
              ...prev,
              geometry: {
                x1: startPoint.x,
                y1: startPoint.y,
                x2: current.x,
                y2: current.y,
              },
            }
          : null
      );
    },
    [isDrawing, previewShape, dragMouseMove, getRelativeCoords]
  );

  /**
   * Handle mouse up - finalize arrow or complete drag
   */
  const handleMouseUp = useCallback(() => {
    // Delegate to drag handler (completes any active drag)
    dragMouseUp();

    // Complete drawing new shape
    if (!previewShape) return;

    // Check if this was just a click (deselect any selected shape)
    if (isAccidentalClick(previewShape)) {
      logger.debug('ArrowTool', 'Click detected - deselecting');
      onValidationError('click'); // Triggers deselection in editor
      setIsDrawing(false);
      setPreviewShape(null);
      startPointRef.current = null;
      return;
    }

    // Validate arrow meets minimum length (pass viewBoxHeight for aspect-ratio-adjusted validation)
    const validation = validateArrow(previewShape.geometry, viewBoxHeight);
    if (!validation.valid) {
      logger.warn('ArrowTool', 'Arrow validation failed:', validation.error);
      onValidationError(validation.error || 'Arrow too short, try again');
    } else {
      logger.info('ArrowTool', 'Arrow created:', previewShape.geometry);
      onShapeComplete(previewShape);
    }

    // Reset state
    setIsDrawing(false);
    setPreviewShape(null);
    startPointRef.current = null;
  }, [previewShape, dragMouseUp, onShapeComplete, onValidationError, viewBoxHeight]);

  /**
   * Handle mouse leave - cancel drawing and clear hover state
   */
  const handleMouseLeaveWrapper = useCallback(() => {
    handleMouseUp(); // Cancel drawing if active
    dragMouseLeave(); // Clear hover state
  }, [handleMouseUp, dragMouseLeave]);

  return (
    <>
      {/* Transparent overlay to capture mouse events */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor,
          zIndex: 15,
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeaveWrapper} // Cancel drawing and clear hover state
      />

      {/* Preview arrow while drawing */}
      {previewShape &&
        (previewShape.geometry.x1 !== previewShape.geometry.x2 ||
          previewShape.geometry.y1 !== previewShape.geometry.y2) && (() => {
          // BUG FIX (#480): Transform preview shape from image space to viewBox space
          const transformedPreview = transformShapeForViewBox(previewShape, photoAspectRatio);

          return (
            <svg
              viewBox={viewBox}
              preserveAspectRatio="xMidYMid meet"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 16,
              }}
            >
              {/* Arrow line */}
              <line
                x1={transformedPreview.geometry.x1}
                y1={transformedPreview.geometry.y1}
                x2={transformedPreview.geometry.x2}
                y2={transformedPreview.geometry.y2}
                stroke={transformedPreview.strokeColor}
                strokeWidth={transformedPreview.strokeWidth}
                strokeDasharray={transformedPreview.strokeDasharray}
                vectorEffect="non-scaling-stroke"
                opacity={0.5}
              />
              {/* Arrow head */}
              <polygon
                points={calculateArrowHead(
                  transformedPreview.geometry.x1,
                  transformedPreview.geometry.y1,
                  transformedPreview.geometry.x2,
                  transformedPreview.geometry.y2
                )}
                fill={transformedPreview.strokeColor}
                stroke={transformedPreview.strokeColor}
                strokeWidth={0.5}
                opacity={0.5}
              />
            </svg>
          );
        })()}
    </>
  );
}
